import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Building2, MapPin } from "lucide-react";

const STAGES = [
  { id: "", label: "Todas etapas" },
  { id: "identificado", label: "Identificado" },
  { id: "contato_feito", label: "Contato feito" },
  { id: "conversa_iniciada", label: "Conversa iniciada" },
  { id: "visita_captacao", label: "Visita de captação" },
  { id: "agenciado", label: "Agenciado" },
];

const STAGE_COLORS = {
  identificado: "bg-white/8 text-white",
  contato_feito: "bg-amber-500/15 text-amber-300",
  conversa_iniciada: "bg-[#3B9EFF]/15 text-[#7FBEFF]",
  visita_captacao: "bg-purple-500/15 text-purple-300",
  agenciado: "bg-emerald-500/15 text-emerald-300",
};

const stageLabel = (s) => STAGES.find((x) => x.id === s)?.label || s;

export default function AgencyAgenciamentos() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [realtors, setRealtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const realtor_id = params.get("realtor_id") || "";
  const stage = params.get("stage") || "";

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (realtor_id) q.set("realtor_id", realtor_id);
    if (stage) q.set("stage", stage);
    api.get(`/agency/agenciamentos?${q.toString()}`)
      .then((r) => setItems(r.data.agenciamentos))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [realtor_id, stage]);
  useEffect(() => { api.get("/agency/realtors").then((r) => setRealtors(r.data.realtors)); }, []);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const filtered = items.filter((a) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (a.title || "").toLowerCase().includes(s) ||
      (a.address || "").toLowerCase().includes(s) ||
      (a.realtor_name || "").toLowerCase().includes(s) ||
      (a.phone || "").toLowerCase().includes(s)
    );
  });

  // Kanban buckets
  const buckets = STAGES.filter((s) => s.id).map((s) => ({
    ...s,
    items: filtered.filter((it) => (it.stage || "identificado") === s.id),
  }));

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="agency-agenciamentos">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-[#3B9EFF] font-bold">Captação de imóveis</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold mt-1">Agenciamentos</h1>
          <p className="text-white/60 text-sm mt-2">
            {items.length} imóve{items.length !== 1 ? "is" : "l"} em captação pelos corretores da imobiliária.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="ag-search-input"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por endereço, corretor ou telefone..."
            className="w-full pl-9 pr-3 py-2.5 bg-[#0F1F3D] border border-white/10 rounded-lg text-white placeholder:text-white/40 text-sm focus:outline-none focus:border-[#3B9EFF]/50"
          />
        </div>
        <select data-testid="ag-realtor-filter" value={realtor_id} onChange={(e) => setFilter("realtor_id", e.target.value)}
          className="px-3 py-2.5 bg-[#0F1F3D] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#3B9EFF]/50">
          <option value="">Todos os corretores</option>
          {realtors.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select data-testid="ag-stage-filter" value={stage} onChange={(e) => setFilter("stage", e.target.value)}
          className="px-3 py-2.5 bg-[#0F1F3D] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#3B9EFF]/50">
          {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="p-8 text-white/60">Carregando agenciamentos...</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center bg-[#0F1F3D] border border-dashed border-white/15 rounded-2xl">
          <Building2 className="w-8 h-8 mx-auto text-white/30 mb-3" />
          <div className="text-white/60">Nenhum agenciamento encontrado com esses filtros.</div>
          <div className="text-white/40 text-xs mt-2">Os corretores capturam imóveis via foto de placa no WhatsApp e aparecem aqui em tempo real.</div>
        </div>
      ) : (
        <>
          {/* Kanban de agenciamentos */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            {buckets.map((b) => (
              <div key={b.id} className="bg-[#0F1F3D] border border-white/10 rounded-2xl p-3" data-testid={`ag-column-${b.id}`}>
                <div className="flex items-center justify-between px-1 pb-2 mb-2 border-b border-white/10">
                  <span className="text-[11px] uppercase tracking-wider font-bold text-white/70">{b.label}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded bg-white/10 text-white/70 font-semibold">{b.items.length}</span>
                </div>
                <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-1">
                  {b.items.length === 0 ? (
                    <div className="text-[11px] text-white/30 italic px-2 py-4 text-center">vazio</div>
                  ) : (
                    b.items.map((it) => (
                      <div key={it.id} className="p-3 bg-[#0A1937] border border-white/8 rounded-lg hover:border-[#3B9EFF]/40 transition" data-testid={`ag-card-${it.id}`}>
                        <div className="flex items-start gap-2">
                          <div className="w-8 h-8 rounded bg-[#3B9EFF]/15 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-[#7FBEFF]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-sm font-semibold truncate">{it.title || it.address || "Imóvel"}</div>
                            {it.address && (
                              <div className="text-white/50 text-[11px] mt-0.5 flex items-start gap-1">
                                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span className="truncate">{it.address}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div className="w-5 h-5 rounded-full bg-[#3B9EFF]/25 text-[#7FBEFF] flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                              {it.realtor_name?.[0]?.toUpperCase() || "?"}
                            </div>
                            <span className="text-white/70 truncate">{it.realtor_name}</span>
                          </div>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${it.days_in_stage >= 3 && it.stage === "contato_feito" ? "bg-amber-500/20 text-amber-300" : "bg-white/8 text-white/50"}`}>
                            {it.days_in_stage}d
                          </span>
                        </div>
                        {it.phone && (
                          <div className="mt-1.5 text-[10px] text-white/40 font-mono">{it.phone}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Tabela alternativa (mobile-friendly) */}
          <div className="bg-[#0F1F3D] border border-white/10 rounded-2xl overflow-hidden md:hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-white/40 font-bold border-b border-white/10">
                    <th className="px-4 py-3">Imóvel</th>
                    <th className="px-4 py-3">Corretor</th>
                    <th className="px-4 py-3">Etapa</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr key={`t-${it.id}`} className="border-b border-white/5">
                      <td className="px-4 py-3">
                        <div className="text-white font-semibold text-xs">{it.title || it.address}</div>
                        <div className="text-white/50 text-[10px]">{it.phone}</div>
                      </td>
                      <td className="px-4 py-3 text-white/70 text-xs">{it.realtor_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-[10px] font-semibold ${STAGE_COLORS[it.stage] || STAGE_COLORS.identificado}`}>
                          {stageLabel(it.stage)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
